import { useEffect, useState } from 'react';

/** Milliseconds from `now` until the next local midnight (always > 0). */
export function msUntilNextMidnight(now: Date): number {
    const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
    return Math.max(1, next.getTime() - now.getTime());
}

/**
 * Today's date, kept current. A value computed once at render goes stale on a
 * screen left open overnight — the workshop display that never reloads would
 * label yesterday "Today". This re-renders at local midnight, and again
 * whenever the tab becomes visible, because a timer does not fire on time
 * across a sleeping laptop or a backgrounded phone.
 */
export function useToday(): Date {
    const [today, setToday] = useState(() => new Date());

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        const refresh = () => {
            setToday(new Date());
            clearTimeout(timer);
            timer = setTimeout(refresh, msUntilNextMidnight(new Date()));
        };
        timer = setTimeout(refresh, msUntilNextMidnight(new Date()));

        const onVisible = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, []);

    return today;
}
